import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown, faStore } from "@fortawesome/free-solid-svg-icons";
import { AssetTableItem } from "../../../types/types";
import { getTypeConfig } from "./typeConfig";
import { nodeMatchesFilter } from "./utils";

export interface AssetTreeNodeProps {
  asset: AssetTableItem;
  depth: number;
  selectedId: number | null;
  expandedIds: Set<number>;
  filterText: string;
  /** Optional type filter – only nodes (or ancestors of nodes) with this type are shown. */
  typeFilter?: string;
  onSelect: (_id: number) => void;
  onToggleExpand: (_id: number) => void;
  onContextMenu: (_e: React.MouseEvent, _assetId: number) => void;
  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  draggedId?: number | null;
  dropTargetId?: number | null;
  /** IDs that must not receive a drop (the dragged node + its descendants). */
  invalidDropIds?: Set<number>;
  onDragStart?: (_id: number, _e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDragOver?: (_id: number, _e: React.DragEvent) => void;
  onDragLeave?: (_id: number, _e: React.DragEvent) => void;
  onDrop?: (_targetId: number, _e: React.DragEvent) => void;
  // ── Selection mode ──────────────────────────────────────────────────────────
  selectionMode?: boolean;
  multiSelectedIds?: Set<number>;
  onToggleMultiSelect?: (_id: number) => void;
}

/** Highlight the matched portion of a name. */
function HighlightedName({ name, query }: { name: string; query: string }): React.ReactElement {
  if (!query) return <>{name}</>;
  const idx = name.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{name}</>;
  return (
    <>
      {name.slice(0, idx)}
      <mark className="bg-amber-400/30 text-amber-200 rounded-sm not-italic">
        {name.slice(idx, idx + query.length)}
      </mark>
      {name.slice(idx + query.length)}
    </>
  );
}

const AssetTreeNode: React.FC<AssetTreeNodeProps> = ({
  asset,
  depth,
  selectedId,
  expandedIds,
  filterText,
  typeFilter,
  onSelect,
  onToggleExpand,
  onContextMenu,
  draggedId = null,
  dropTargetId = null,
  invalidDropIds = new Set(),
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  selectionMode = false,
  multiSelectedIds = new Set(),
  onToggleMultiSelect,
}) => {
  const cfg = getTypeConfig(asset.type);
  const hasChildren = !!asset.children && asset.children.length > 0;
  const isExpanded = expandedIds.has(asset.id);
  const isSelected = selectedId === asset.id;
  const isSelectedInMulti = multiSelectedIds.has(asset.id);

  const isDragging = draggedId === asset.id;
  const isDropTarget = dropTargetId === asset.id;
  const isInvalid = draggedId !== null && invalidDropIds.has(asset.id);

  // When filtering, only render if this node (or a descendant) matches
  if ((filterText || typeFilter) && !nodeMatchesFilter(asset, filterText, typeFilter)) return null;

  const visibleChildren =
    hasChildren && isExpanded
      ? asset.children!.filter(
          (c) => (!filterText && !typeFilter) || nodeMatchesFilter(c, filterText, typeFilter)
        )
      : [];

  return (
    <>
      <div
        role="treeitem"
        aria-selected={isSelected}
        draggable={!!onDragStart}
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
        className={`flex items-center gap-1 py-[3px] px-1 mb-[1px] rounded cursor-pointer select-none transition-colors
          ${isDragging ? "opacity-40" : ""}
          ${isDropTarget && !isInvalid ? "ring-1 ring-amber-400 bg-amber-400/10" : ""}
          ${isSelected && !isDropTarget ? "bg-amber-400/14" : ""}
          ${!isSelected && !isDropTarget ? "hover:bg-white/5" : ""}
        `}
        onClick={() => {
          if (selectionMode) {
            onToggleMultiSelect?.(asset.id);
          } else {
            onSelect(asset.id);
          }
        }}
        onContextMenu={(e) => onContextMenu(e, asset.id)}
        onDragStart={(e) => {
          if (onDragStart) {
            onDragStart(asset.id, e);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(asset.id));
            e.stopPropagation();
          }
        }}
        onDragEnd={() => onDragEnd?.()}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!onDragOver || isInvalid || draggedId === null) {
            e.dataTransfer.dropEffect = "none";
            return;
          }
          e.dataTransfer.dropEffect = "move";
          onDragOver(asset.id, e);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            onDragLeave?.(asset.id, e);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = parseInt(e.dataTransfer.getData("text/plain"), 10);
          if (!id || invalidDropIds.has(asset.id)) return;
          onDrop?.(asset.id, e);
        }}
      >
        {/* Chevron / leaf indicator */}
        <span
          className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-zinc-500 hover:text-zinc-300"
          onClick={(e) => {
            if (hasChildren) {
              e.stopPropagation();
              onToggleExpand(asset.id);
            }
          }}
        >
          {hasChildren ? (
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              className="text-[10px]"
            />
          ) : (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-700" />
          )}
        </span>

        {/* Selection Checkbox */}
        {selectionMode && (
          <div
            className={`w-3.5 h-3.5 border rounded flex-shrink-0 flex items-center justify-center transition-colors ${
              isSelectedInMulti ? "bg-amber-400 border-amber-400" : "bg-zinc-800 border-zinc-700"
            }`}
          >
            {isSelectedInMulti && <div className="w-1.5 h-1.5 bg-zinc-950 rounded-sm" />}
          </div>
        )}

        {/* Type icon */}
        <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
          <FontAwesomeIcon icon={cfg.icon} className="text-[10px]" />
        </span>

        {/* Name */}
        <span
          className={`flex-1 text-xs truncate ml-0.5 ${isSelected ? "text-amber-400" : "text-zinc-200"}`}
        >
          <HighlightedName name={asset.name} query={filterText} />
          {asset.isFromMarketplace && (
            <FontAwesomeIcon
              icon={faStore}
              className="ml-1.5 text-amber-500/80 text-[10px]"
              title="Acquired from Marketplace"
            />
          )}
        </span>

        {/* Collapsed child-count badge */}
        {hasChildren && !isExpanded && !filterText && (
          <span className="text-[10px] text-zinc-600 bg-zinc-800 rounded px-1 flex-shrink-0">
            {asset.children!.length}
          </span>
        )}
      </div>

      {/* Recursive children */}
      {(isExpanded || filterText || typeFilter) &&
        visibleChildren.map((child) => (
          <AssetTreeNode
            key={child.id}
            asset={child}
            depth={depth + 1}
            selectedId={selectedId}
            expandedIds={expandedIds}
            filterText={filterText}
            typeFilter={typeFilter}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            onContextMenu={onContextMenu}
            selectionMode={selectionMode}
            multiSelectedIds={multiSelectedIds}
            onToggleMultiSelect={onToggleMultiSelect}
            draggedId={draggedId}
            dropTargetId={dropTargetId}
            invalidDropIds={invalidDropIds}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          />
        ))}
    </>
  );
};

export default AssetTreeNode;
